import { Box, Button, ButtonBase, Grid, SvgIcon, Tab, Tabs, Typography } from '@mui/material';
import UploadIcon from 'assets/icons/upload.svg';
import Loading from 'components/common/Loading';
import FormikAutosuggest from 'components/formik/Autosuggest';
import FormikChipInput from 'components/formik/ChipInput';
import FormikDropzoneBox from 'components/formik/DropzoneBox';
import Editor from 'components/formik/Editor';
import Language from 'components/formik/Language';
import Switch from 'components/formik/Switch';
import TextField from 'components/formik/TextField';
import TimeZone from 'components/formik/TimeZone';
import TeamSyncButton from './TeamSyncButton';
import EmployeeSyncButton from './EmployeeSyncButton';
import SanitizeCompanyButton from './SanitizeCompanyButton';
import FtpUploadButton from './FtpUploadButton';
import ESignatureProviderConfig from './ESignatureProviderConfig';
import CompanyAiOverridePanel from './CompanyAiOverridePanel';
import ReactionsConfig from './ReactionsConfig';
import FeatureFlagsField from './FeatureFlagsField';
import applyCompanyInfoFromFetch from './applyCompanyInfoFromFetch';
import { Field, Form, useFormikContext } from 'formik';
import capitalize from 'lodash/capitalize';
import debounce from 'lodash/debounce';
import startCase from 'lodash/startCase';
import { array, bool, func, object, string } from 'prop-types';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { apiUrl } from 'services/apiBaseUrl';
import companyShape from 'shapes/shortCompany';
import { I18n } from 'utils/i18n';
import removeAccents from 'utils/removeAccents';
import useFileUrl from 'utils/useFileUrl';

const EditorComponent = props => (
  <Suspense fallback={<Loading />}>
    <Editor {...props} />
  </Suspense>
);
const uploadPlaceholder = (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <SvgIcon sx={{ mr: 1 }}>
      <UploadIcon />
    </SvgIcon>
    <Typography>{I18n.t('AcknowledgementCreate.Drag & drop file here or')}</Typography>
    &nbsp;
    <ButtonBase sx={{ color: 'primary.main' }}>
      {I18n.t('AcknowledgementCreate.Browse file')}
    </ButtonBase>
  </Box>
);
const ImagePreviewUpload = props => {
  const image = useFileUrl(props.form.values.onBoardingImage);

  let backgroundImage = 'none';
  if (props.field.name === 'logo') {
    backgroundImage = `linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.4))`;
    if (image && props.field.value) {
      backgroundImage += `, url(${image})`;
    }
  }
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        display: 'flex',
        flexGrow: 1,
        alignItems: 'center',
        backgroundColor: 'secondary.ultraLight',
        backgroundImage,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% auto',
        backgroundPosition: '0 100%',
        height: 80,
      }}
    >
      <FormikDropzoneBox
        sx={{
          height: 70,
          minHeight: 70,
          '& > div': {
            padding: 5,
          },
        }}
        accept="image/*"
        placeholder={uploadPlaceholder}
        {...props}
      />
    </Box>
  );
};

ImagePreviewUpload.propTypes = {
  form: object.isRequired,
  field: object.isRequired,
};

const FormField = ({ field, isLoading }) => {
  const { getFieldProps } = useFormikContext();
  const { value } = getFieldProps(field.name);

  // Special handling for ESignatureProviderConfig
  if (field.component === 'ESignatureProviderConfig') {
    return <ESignatureProviderConfig />;
  }

  if (field.component === 'ReactionsConfig') {
    return (
      <>
        <Grid size={2}>
          <Typography>{I18n.t('CompanyForm.Reactions.Title')}</Typography>
        </Grid>
        <Grid size={10}>
          <ReactionsConfig />
        </Grid>
      </>
    );
  }

  return (
    <>
      <Grid size={2}>
        <Typography>{startCase(field.name)}</Typography>
      </Grid>
      <Grid size={10}>
        <Field
          disabled={isLoading && ['name', 'subDomain', 'logo', 'logoDark'].includes(field.name)}
          slotProps={{
            input: {
              endAdornment: field.name === 'webSiteUrl' && isLoading ? <Loading size={20} /> : null,
            },
            inputLabel: { shrink: true },
          }}
          label={
            field.name === 'subDomain' && value ? `https://${value}.web.ommnio.com` : undefined
          }
          name={field.name}
          component={field.component || TextField}
          {...(field.props || {})}
        />
      </Grid>
    </>
  );
};
FormField.propTypes = {
  field: object.isRequired,
  isLoading: bool.isRequired,
};

const COMPANY_FORM_TAB = {
  GENERAL: 'general',
  BILLING: 'billing',
  SUBSCRIPTION: 'subscription',
  SETTINGS: 'settings',
  ESIGNATURE: 'esignature',
  AI: 'ai',
  LEGAL: 'legal',
};

const COMPANY_FORM_TABS = [
  { id: COMPANY_FORM_TAB.GENERAL, labelKey: 'CompanyForm.TabGeneral' },
  { id: COMPANY_FORM_TAB.BILLING, labelKey: 'CompanyForm.TabBilling' },
  { id: COMPANY_FORM_TAB.SUBSCRIPTION, labelKey: 'CompanyForm.TabSubscription' },
  { id: COMPANY_FORM_TAB.SETTINGS, labelKey: 'CompanyForm.TabSettings' },
  { id: COMPANY_FORM_TAB.ESIGNATURE, labelKey: 'CompanyForm.TabESignature' },
  { id: COMPANY_FORM_TAB.AI, labelKey: 'CompanyForm.TabAI' },
  { id: COMPANY_FORM_TAB.LEGAL, labelKey: 'CompanyForm.TabLegal' },
];

const fields = (templates, isNewCompany) => {
  const T = COMPANY_FORM_TAB;
  return [
    { name: 'webSiteUrl', tab: T.GENERAL },
    { name: 'name', tab: T.GENERAL },
    { name: 'subDomain', tab: T.GENERAL },
    { name: 'contactEmail', tab: T.GENERAL },
    { name: 'dpoEmail', tab: T.GENERAL },
    { name: 'supportEmail', tab: T.GENERAL },
    {
      name: 'codeType',
      tab: T.GENERAL,
      component: FormikAutosuggest,
      props: {
        label: I18n.t('CompanyForm.CodeType'),
        fullWidth: true,
        suggestions: [
          { label: I18n.t('CompanyForm.CodeTypes.Number'), value: 'number' },
          { label: I18n.t('CompanyForm.CodeTypes.Alphanumeric'), value: 'alphanumeric' },
          { label: I18n.t('CompanyForm.CodeTypes.SpanishSSN'), value: 'spanishSSN' },
          { label: I18n.t('CompanyForm.CodeTypes.String'), value: 'string' },
        ],
      },
    },
    {
      name: 'featureFlags',
      tab: T.GENERAL,
      component: FeatureFlagsField,
    },
    {
      name: 'divisions',
      tab: T.GENERAL,
      component: FormikChipInput,
      props: { blurBehavior: 'add-or-clear' },
    },
    {
      name: 'logoDark',
      tab: T.GENERAL,
      component: ImagePreviewUpload,
    },
    {
      name: 'logo',
      tab: T.GENERAL,
      component: ImagePreviewUpload,
    },
    {
      name: 'onBoardingImage',
      tab: T.GENERAL,
      component: ImagePreviewUpload,
    },
    { name: 'businessName', tab: T.BILLING },
    { name: 'billingContactName', tab: T.BILLING },
    { name: 'billingContactSurname', tab: T.BILLING },
    { name: 'address', tab: T.BILLING },
    { name: 'city', tab: T.BILLING },
    { name: 'zip', tab: T.BILLING },
    { name: 'state', tab: T.BILLING },
    { name: 'country', tab: T.BILLING },
    { name: 'vatIDNumber', tab: T.BILLING },
    { name: 'stripeCustomerId', tab: T.BILLING },
    { name: 'stock.digitalSignatures', tab: T.BILLING },
    {
      name: 'subscription.product.name',
      tab: T.SUBSCRIPTION,
      component: FormikAutosuggest,
      props: {
        isClearable: true,
        suggestions: [
          {
            label: '(None)',
            value: null,
          },
          {
            label: 'SME',
            value: 'SME',
          },
          {
            label: 'Big teams',
            value: 'Big teams',
          },
          {
            label: 'Enterprise',
            value: 'Enterprise',
          },
        ],
      },
    },
    {
      name: 'subscription.status',
      tab: T.SUBSCRIPTION,
      component: FormikAutosuggest,
      props: {
        isClearable: true,
        suggestions: [
          'active',
          'canceled',
          'incomplete',
          'incomplete_expired',
          'past_due',
          'trialing',
          'unpaid',
        ].map(value => ({
          label: value,
          value,
        })),
      },
    },
    {
      name: 'language',
      tab: T.SETTINGS,
      component: Language,
    },
    {
      name: 'languages',
      tab: T.SETTINGS,
      component: Language,
      props: {
        isMulti: true,
      },
    },
    {
      name: 'timeZone',
      tab: T.SETTINGS,
      component: TimeZone,
    },
    {
      name: 'reactions',
      tab: T.SETTINGS,
      component: 'ReactionsConfig',
      fullWidth: true,
    },
    {
      name: 'defaultMobileNotifications',
      tab: T.SETTINGS,
      component: FormikAutosuggest,
      props: {
        sx: { flexGrow: 1 },
        isClearable: true,
        suggestions: ['Always', 'Restricted', 'Never'].map(value => ({
          label: capitalize(value),
          value,
        })),
      },
    },
    { name: 'appName', tab: T.SETTINGS },
    { name: 'appLogo', tab: T.SETTINGS },
    {
      name: 'allowAnonymousEmployees',
      tab: T.SETTINGS,
      component: Switch,
    },
    {
      name: 'allowOnlyAuthorizedDevices',
      tab: T.SETTINGS,
      component: Switch,
    },
    {
      name: 'demo',
      tab: T.SETTINGS,
      component: Switch,
    },
    {
      name: 'template',
      tab: T.SETTINGS,
      component: Switch,
    },
    ...(isNewCompany
      ? [
          {
            name: 'fromTemplate',
            tab: T.SETTINGS,
            component: FormikAutosuggest,
            props: {
              isClearable: true,
              suggestions: templates.map(t => ({
                label: t.name,
                value: t._id,
              })),
            },
          },
        ]
      : []),
    {
      name: 'eSignatureProvider',
      tab: T.ESIGNATURE,
      component: 'ESignatureProviderConfig',
      fullWidth: true,
    },
    {
      name: 'terms',
      tab: T.LEGAL,
      component: EditorComponent,
    },
    {
      name: 'privacy',
      tab: T.LEGAL,
      component: EditorComponent,
    },
  ];
};

const fetchCompanyInfo = url => {
  const request = new Request(apiUrl(`/v1/company-info?url=${encodeURIComponent(url)}`), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return fetch(request).then(response => response.json());
};

const fetchCompanyInfoDebounced = debounce(
  (company, values, touched, setFieldValue, setLoading) => {
    return new Promise(resolve => {
      if (company?.webSiteUrl !== values.webSiteUrl) {
        resolve(
          fetchCompanyInfo(values.webSiteUrl).then(info =>
            applyCompanyInfoFromFetch({ info, values, touched, setFieldValue }),
          ),
        );
      }
      resolve(null);
    })
      .catch(() => {})
      .then(() => {
        setLoading(false);
      });
  },
  1000,
);

export const CompanyForm = ({ company, templates, onClose, error, isLoading, isError }) => {
  const { values, touched, setFieldValue } = useFormikContext();
  const [isLoadingCompany, setLoadingCompany] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const allFields = useMemo(() => fields(templates, !values._id), [templates, values._id]);
  const activeTab = COMPANY_FORM_TABS[tabIndex];
  useEffect(() => {
    if (company?.webSiteUrl !== values.webSiteUrl) {
      setLoadingCompany(true);
    }
    fetchCompanyInfoDebounced(company, values, touched, setFieldValue, setLoadingCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced fetch keyed on webSiteUrl only; full formik deps retrigger loops
  }, [values.webSiteUrl]);
  useEffect(() => {
    if ((!touched.subDomain || !values.subDomain) && values.name) {
      const subDomain = removeAccents(values.name).toLowerCase().replace(/\s/g, '-');
      setFieldValue('subDomain', subDomain);
    }
  }, [setFieldValue, touched.subDomain, values.name, values.subDomain]);
  return (
    <Form
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          mt: 2,
          flex: '1 1 auto',
          overflowY: 'scroll',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, next) => setTabIndex(next)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          {COMPANY_FORM_TABS.map(tab => (
            <Tab
              key={tab.id}
              id={`company-form-tab-${tab.id}`}
              aria-controls={`company-form-tabpanel-${tab.id}`}
              label={I18n.t(tab.labelKey)}
            />
          ))}
        </Tabs>
        {activeTab && (
          <Box
            role="tabpanel"
            id={`company-form-tabpanel-${activeTab.id}`}
            aria-labelledby={`company-form-tab-${activeTab.id}`}
            sx={{ pt: 2, flex: '1 1 auto', minHeight: 0 }}
          >
            <Grid
              spacing={1}
              sx={{
                flex: '1 1 auto',
                display: 'flex',
                '& > .MuiGrid-root': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              container
            >
              {company?._id && activeTab.id === COMPANY_FORM_TAB.GENERAL && (
                <>
                  <Grid size={2}>
                    <Typography>{I18n.t('CompanyForm.Id')}</Typography>
                  </Grid>
                  <Grid size={10}>
                    <Field name="_id" disabled component={TextField} />
                  </Grid>
                </>
              )}
              {company?._id && activeTab.id === COMPANY_FORM_TAB.AI && (
                <Grid size={12}>
                  <CompanyAiOverridePanel companyId={company._id} />
                </Grid>
              )}
              {allFields
                .filter(f => f.tab === activeTab.id)
                .map(field => (
                  <FormField key={field.name} field={field} isLoading={isLoadingCompany} />
                ))}
            </Grid>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          pt: 2,
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          {company?._id && company?.teamSyncEnabled && (
            <TeamSyncButton companyId={company._id} disabled={isLoading} />
          )}
          {company?._id && company?.employeeSyncEnabled && (
            <EmployeeSyncButton disabled={isLoading} />
          )}
          {company?._id && <SanitizeCompanyButton companyId={company._id} disabled={isLoading} />}
          {company?._id && company?.ftpSyncEnabled && (
            <FtpUploadButton companyId={company._id} disabled={isLoading} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isError && (
            <Typography sx={{ flexGrow: 1 }} color="error">
              {error?.data?.message || I18n.t('CompanyForm.Unexpected error')}
            </Typography>
          )}
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
            sx={{ color: 'primary.ultraDark' }}
          >
            {I18n.t('CompanyForm.Cancel')}
          </Button>
          <Button disabled={isLoading} type="submit" variant="contained" color="primary">
            {I18n.t('CompanyForm.Save')}
            {isLoading && <Loading sx={{ ml: 1 }} size={14} color="secondary" />}
          </Button>
        </Box>
      </Box>
    </Form>
  );
};

CompanyForm.propTypes = {
  company: companyShape.isRequired,
  onClose: func.isRequired,
  error: string.isRequired,
  isLoading: bool.isRequired,
  isError: bool.isRequired,
  templates: array.isRequired,
};
